import {
  RequestOutcome,
  trackRequest,
} from '@/context/useConnectionQualityStore'
import { hasCookieConsent } from '@/libs/analytics/analytics'
import axios from 'axios'
import { apiColour } from './apiColour'
import { tokenStore } from './tokenStore'

// Local dev goes through the craco proxy (see craco.config.js) so the refresh
// cookie is same-origin; the browser drops it otherwise.
const isLocalhost =
  typeof window !== 'undefined' &&
  /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname)

const baseURL =
  process.env.NODE_ENV === 'development' && isLocalhost
    ? '/'
    : process.env.REACT_APP_API_URL

export const apiClient = axios.create({
  baseURL,
  withCredentials: true, // send up_refresh cookie on every request to /upauth/session/*
})

apiClient.interceptors.request.use((config) => {
  if (hasCookieConsent()) {
    const cid = localStorage.getItem('ga_client_id')
    if (cid) {
      config.headers['x-session-id'] = cid
    }
  }
  const colour = apiColour()
  if (colour) {
    config.headers['X-Up-Color'] = colour
  }
  return config
})

const requestTrackers = new WeakMap<object, (outcome: RequestOutcome) => void>()

apiClient.interceptors.request.use((config) => {
  requestTrackers.set(config, trackRequest())
  return config
})

apiClient.interceptors.response.use(
  (response) => {
    requestTrackers.get(response.config)?.('ok')
    return response
  },
  (error) => {
    // Any HTTP status means the server was reached; only no response is a failure
    const outcome = axios.isCancel(error)
      ? 'cancelled'
      : error.response
        ? 'ok'
        : 'failed'
    if (error.config) requestTrackers.get(error.config)?.(outcome)
    return Promise.reject(error)
  },
)

const refreshAccessToken = async (): Promise<string> => {
  const response = await apiClient.post('upauth/session/refresh')
  const newToken = response.data.access
  tokenStore.set(newToken)
  return newToken
}

const executeApiRequest = async (
  endpoint: string,
  method: string,
  token: string | null,
  data?: any,
  headers = {},
  signal?: AbortSignal,
  responseType?: any,
) => {
  const requestHeaders: Record<string, any> = { ...headers }

  if (token) {
    requestHeaders.Authorization = `Bearer ${token}`
  }

  try {
    const response = await apiClient({
      method,
      url: endpoint,
      data,
      headers: requestHeaders,
      signal,
      responseType,
    })
    return response.data
  } catch (error: any) {
    if (axios.isCancel(error)) {
      const abortError = new Error('Request aborted')
      abortError.name = 'AbortError'
      throw abortError
    }

    if (error.response) {
      if (error.response.status === 401) {
        error.isTokenExpired = true
      }
      throw error
    }
    throw error
  }
}

export const requestWithRetry = async (
  endpoint: string,
  method: string,
  data?: any,
  headers = {},
  signal?: AbortSignal,
  responseType?: any,
) => {
  let token = tokenStore.get()

  // Cleared in `finally` so a won race leaves no pending rejection behind.
  let timeoutId: ReturnType<typeof setTimeout> | undefined

  try {
    const response = await Promise.race([
      executeApiRequest(
        endpoint,
        method,
        token,
        data,
        headers,
        signal,
        responseType,
      ),
      new Promise((_, reject) => {
        timeoutId = setTimeout(
          () => reject(new Error('Request timed out')),
          30000,
        )
      }),
    ])
    return response
  } catch (error: any) {
    if (error.name === 'AbortError') throw error

    if (error.isTokenExpired) {
      try {
        token = await refreshAccessToken()
        return await executeApiRequest(
          endpoint,
          method,
          token,
          data,
          headers,
          signal,
          responseType,
        )
      } catch (refreshError) {
        console.log('Token refresh failed:', refreshError)
        tokenStore.clear()
        localStorage.removeItem('user')
        if (localStorage.getItem('iframe_mode') === 'true') {
          window.parent.postMessage({ type: 'SESSION_EXPIRED' }, '*')
          throw refreshError
        }
        window.location.href = '/login'
      }
    }

    throw error
  } finally {
    if (timeoutId) clearTimeout(timeoutId)
  }
}
